import * as React from 'react';

import {
  enumPaymentSortOption,
  PaymentSortOption,
} from '@rematter/frontend-lib-api-client';
import { FinanceRootLayout } from '@rematter/modules/finance/_components/_layouts/FinanceRootLayout';
import {
  Button,
  Content,
  DataGrid,
  Footer,
  Pagination,
  type PaginationProps,
  SearchField,
  Toolbar,
  useMemoDataGridRows,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle } from 'lucide-react';

import { paymentsConfirmAtom } from '../../_graphql/mutationPaymentsConfirm';
import { paymentsUnconfirmAtom } from '../../_graphql/mutationPaymentsUnconfirm';
import * as state from '../../_state/financePayments.state';
import * as formState from '../../_state/paymentForm.state';
import { financeRoutes } from '../../routes';

import { PaymentWizard } from './_PaymentWizard';
import { GenerateAchFileDialog } from './GenerateAchFileDialog';
import { usePaymentsListColumns } from './hooks/usePaymentsListColumns';
import { PaymentApplyToBillDialog } from './PaymentApplyToBillDialog';
import { PaymentApplyToInvoiceDialog } from './PaymentApplyToInvoiceDialog';
import { PaymentsBulkToolbar } from './PaymentsBulkToolbar';
import { PrintChecksDialog } from './PrintChecksDialog';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { PaymentRecord } from '~/modules/finance/types';
import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const COLUMN_TO_SORT_MAP: Record<
  string,
  { asc: PaymentSortOption; desc: PaymentSortOption }
> = {
  transactionDate: {
    asc: enumPaymentSortOption.DateAsc,
    desc: enumPaymentSortOption.DateDesc,
  },
  status: {
    asc: enumPaymentSortOption.StatusAsc,
    desc: enumPaymentSortOption.StatusDesc,
  },
  amount: {
    asc: enumPaymentSortOption.AmountAsc,
    desc: enumPaymentSortOption.AmountDesc,
  },
};

export const PaymentsListView = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();
  const toast = useToast();

  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedKeys);
  const [searchTerm, setSearchTerm] = useDebouncedAtom(state.$searchTerm, 500);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [isWizardOpen, setIsWizardOpen] = useAtom(
    formState.$isPaymentWizardOpen,
  );
  const resetPaymentForm = useSetAtom(formState.$resetPaymentForm);
  const setCurrentSorting = useSetAtom(state.$currentSorting);

  const {
    data: { payments, total } = { payments: [], total: 0 },
    error,
    refetch: refetchPayments,
    isFetching,
    loadingState,
  } = useAtomValue(state.$remotePayments);

  const [selectedPaymentForApply, setSelectedPaymentForApply] =
    React.useState<PaymentRecord | null>(null);
  const [showApplyToBillDialog, setShowApplyToBillDialog] =
    React.useState(false);
  const [showApplyToInvoiceDialog, setShowApplyToInvoiceDialog] =
    React.useState(false);
  const [showAchFileDialog, setShowAchFileDialog] = React.useState(false);
  const [showPrintChecksDialog, setShowPrintChecksDialog] =
    React.useState(false);

  const $confirmPayments = React.useMemo(
    () =>
      paymentsConfirmAtom({
        onSuccess: () => {
          refetchPayments?.();
          setSelectedKeys(new Set());
        },
      }),
    [refetchPayments, setSelectedKeys],
  );
  const confirmPayments = useSetAtom($confirmPayments);

  const $unconfirmPayments = React.useMemo(
    () =>
      paymentsUnconfirmAtom({
        onSuccess: () => {
          refetchPayments?.();
          setSelectedKeys(new Set());
        },
      }),
    [refetchPayments, setSelectedKeys],
  );
  const unconfirmPayments = useSetAtom($unconfirmPayments);

  const selectedRows = useMemoDataGridRows(payments, selectedKeys);

  const handleApplyAdvance = React.useCallback((payment: PaymentRecord) => {
    setSelectedPaymentForApply(payment);
    if (
      payment.paymentType === 'AdvanceOut' ||
      payment.paymentType === 'Bill'
    ) {
      setShowApplyToBillDialog(true);
    } else if (
      payment.paymentType === 'AdvanceIn' ||
      payment.paymentType === 'Invoice'
    ) {
      setShowApplyToInvoiceDialog(true);
    }
  }, []);

  const handleBulkConfirm = React.useCallback(
    async (rows: PaymentRecord[]) => {
      try {
        const result = await confirmPayments({ ids: rows.map((r) => r.id) });
        const data = result?.financePaymentsConfirm;
        if (data && data.failedCount > 0) {
          toast.warning(
            t.payments.list.bulkActions.partialFailure({
              failedCount: data.failedCount,
              totalCount: rows.length,
            }),
          );
        } else {
          toast.success(
            t.payments.list.bulkActions.confirmSuccess({
              count: data?.successCount ?? rows.length,
            }),
          );
        }
      } catch {
        toast.error(t.payments.list.error('Failed to confirm payments'));
      }
    },
    [confirmPayments, toast, t],
  );

  const handleBulkUnconfirm = React.useCallback(
    async (rows: PaymentRecord[]) => {
      try {
        const result = await unconfirmPayments({
          ids: rows.map((r) => r.id),
        });
        const data = result?.financePaymentsUnconfirm;
        if (data && data.failedCount > 0) {
          toast.warning(
            t.payments.list.bulkActions.partialFailure({
              failedCount: data.failedCount,
              totalCount: rows.length,
            }),
          );
        } else {
          toast.success(
            t.payments.list.bulkActions.unconfirmSuccess({
              count: data?.successCount ?? rows.length,
            }),
          );
        }
      } catch {
        toast.error(t.payments.list.error('Failed to unconfirm payments'));
      }
    },
    [unconfirmPayments, toast, t],
  );

  const handlePaymentClick = React.useCallback(
    (payment: PaymentRecord) => {
      navigate(financeRoutes.paymentDetail.replace(':id', payment.id));
    },
    [navigate],
  );

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumPaymentSortOption.DateDesc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          setCurrentSorting([
            direction === 'ascending' ? sortMap.asc : sortMap.desc,
          ]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setOffset(0);
  };

  const handleCreateClick = () => {
    resetPaymentForm();
    setIsWizardOpen(true);
  };

  const handleWizardClose = () => {
    setIsWizardOpen(false);
  };

  const { columns } = usePaymentsListColumns({
    onApplyAdvance: handleApplyAdvance,
    onDoubleClick: handlePaymentClick,
  });

  return (
    <FinanceRootLayout className="@PaymentsListView">
      <Toolbar>
        <div className="flex w-full items-center gap-4">
          <SearchField
            autoFocus
            aria-label={t.payments.list.search.label}
            className="min-h-0 w-80"
            placeholder={t.payments.list.search.placeholder}
            value={searchTerm}
            onChange={handleSearch}
          />
          <div className="ml-auto">
            <Button
              color="primary"
              variant="solid"
              icon={<LucidePlusCircle className="size-4" />}
              onPress={handleCreateClick}
            >
              {t.payments.list.buttons.create}
            </Button>
          </div>
        </div>
      </Toolbar>

      <Content>
        <DataGrid<PaymentRecord>
          columns={columns}
          dataSource={payments}
          rowKey="id"
          aria-label={t.payments.list.title}
          selectionMode="multiple"
          onSelectionChange={setSelectedKeys}
          onColumnSort={handleColumnSort}
          fullWidth
          isLoading={loadingState?.isInitialLoading || isFetching}
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.payments.list.error(error.message),
                  centered: true,
                }
              : undefined
          }
        />
      </Content>

      <Footer>
        <Pagination
          limit={limit}
          offset={offset}
          total={total}
          onChange={handlePaginationChange}
        />
      </Footer>

      <PaymentsBulkToolbar
        selectedRows={selectedRows}
        onConfirm={handleBulkConfirm}
        onUnconfirm={handleBulkUnconfirm}
        onGenerateAchFile={() => setShowAchFileDialog(true)}
        onPrintChecks={() => setShowPrintChecksDialog(true)}
        onClearSelection={() => setSelectedKeys(new Set())}
      />

      <PaymentWizard
        isOpen={isWizardOpen}
        onClose={handleWizardClose}
        onSuccess={() => refetchPayments?.()}
      />

      <GenerateAchFileDialog
        isOpen={showAchFileDialog}
        onOpenChange={setShowAchFileDialog}
        selectedPaymentIds={selectedRows
          .filter((r) => r.paymentMethod === 'ACH')
          .map((r) => r.id)}
        onSuccess={() => {
          refetchPayments?.();
          setSelectedKeys(new Set());
        }}
      />

      <PrintChecksDialog
        isOpen={showPrintChecksDialog}
        onOpenChange={setShowPrintChecksDialog}
        selectedPaymentIds={selectedRows
          .filter((r) => r.paymentMethod === 'Check')
          .map((r) => r.id)}
        onSuccess={() => {
          refetchPayments?.();
          setSelectedKeys(new Set());
        }}
      />

      {selectedPaymentForApply && (
        <>
          <PaymentApplyToBillDialog
            isOpen={showApplyToBillDialog}
            onOpenChange={setShowApplyToBillDialog}
            paymentId={selectedPaymentForApply.id}
            unappliedAmount={
              selectedPaymentForApply.unappliedAmount ?? undefined
            }
            accountId={selectedPaymentForApply.accountId ?? undefined}
            currencyCode={selectedPaymentForApply.amount?.currencyCode}
            onSuccess={() => refetchPayments?.()}
          />
          <PaymentApplyToInvoiceDialog
            isOpen={showApplyToInvoiceDialog}
            onOpenChange={setShowApplyToInvoiceDialog}
            paymentId={selectedPaymentForApply.id}
            unappliedAmount={
              selectedPaymentForApply.unappliedAmount ?? undefined
            }
            accountId={selectedPaymentForApply.accountId ?? undefined}
            currencyCode={selectedPaymentForApply.amount?.currencyCode}
            onSuccess={() => refetchPayments?.()}
          />
        </>
      )}
    </FinanceRootLayout>
  );
};
